import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Form, Button, Card, Alert } from "react-bootstrap";
import SearchSelect from "components/common/SearchSelect";
import Spinner from "components/common/Spinner";

import {
    fetchPromptKeys,
    fetchPromptByKey,
    updatePromptByKey,
    clearPrompts,
    clearSinglePrompt
} from "api/menu/hr/prompts/actions";

class PromptsList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedKey: null,
            promptText: "",
            isEditing: false,
            hasChanges: false
        };
    }

    componentDidMount() {
        this.props.fetchPromptKeys();
    }

    componentWillUnmount() {
        this.props.clearPrompts();
        this.props.clearSinglePrompt();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.selectedPrompt && nextProps.selectedPrompt.key) {
            this.setState({
                promptText: nextProps.selectedPrompt.prompt || "",
                hasChanges: false
            });
        }
    }

    handleKeySelection = (selectedOption) => {
        const key = selectedOption ? selectedOption.value : null;
        this.setState({ 
            selectedKey: key,
            isEditing: false,
            hasChanges: false
        });
        
        if (key) {
            this.props.fetchPromptByKey(key);
        } else {
            this.props.clearSinglePrompt();
            this.setState({ promptText: "" });
        }
    };

    handlePromptChange = (e) => {
        this.setState({ 
            promptText: e.target.value,
            hasChanges: true
        });
    };

    handleStartEdit = () => {
        this.setState({ isEditing: true });
    };

    handleCancelEdit = () => {
        this.setState({ 
            isEditing: false,
            promptText: this.props.selectedPrompt.prompt || "",
            hasChanges: false
        });
    };

    handleSave = () => {
        if (!this.state.selectedKey || !this.state.promptText.trim()) {
            return;
        }

        const updateData = {
            prompt: this.state.promptText.trim()
        };

        this.props.updatePromptByKey(this.state.selectedKey, updateData, (success) => {
            if (success) {
                this.setState({ 
                    isEditing: false,
                    hasChanges: false
                });
                // Refresh the prompt data to get updated timestamps
                this.props.fetchPromptByKey(this.state.selectedKey);
            }
        });
    };

    render() {
        const { promptKeys, selectedPrompt, loading } = this.props;
        const { selectedKey, promptText, isEditing, hasChanges } = this.state;

        const keyOptions = promptKeys.map(key => ({ value: key, label: key }));
        const currentKey = keyOptions.find(option => option.value === selectedKey) || null;

        return (
            <div className="dashboard">
                <div className="position-relative">
                    {loading && <Spinner />}
                    
                    <h3 className="mb-4">ATS Prompts Management</h3>
                    
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Select Prompt to Edit</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group>
                                <Form.Label>Prompt Key</Form.Label>
                                <SearchSelect
                                    className="custom-react-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    isMulti={false}
                                    options={keyOptions}
                                    value={currentKey}
                                    onChange={this.handleKeySelection}
                                    placeholder="Select a prompt key to edit..."
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    {selectedKey && selectedPrompt.key && (
                        <Card className="mt-4">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Prompt: {selectedPrompt.key}</h5>
                                <div>
                                    {!isEditing ? (
                                        <Button variant="primary" onClick={this.handleStartEdit}>
                                            Edit Prompt
                                        </Button>
                                    ) : (
                                        <div>
                                            <Button 
                                                variant="secondary" 
                                                className="mr-2" 
                                                onClick={this.handleCancelEdit}
                                            >
                                                Cancel
                                            </Button>
                                            <Button 
                                                variant="success" 
                                                onClick={this.handleSave}
                                                disabled={!hasChanges || !promptText.trim()}
                                            >
                                                Save Changes
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {selectedPrompt.createdUtc && (
                                    <div className="mb-3 text-muted small">
                                        <div>Created: {new Date(selectedPrompt.createdUtc).toLocaleString()}</div>
                                        {selectedPrompt.updatedUtc && (
                                            <div>Last Updated: {new Date(selectedPrompt.updatedUtc).toLocaleString()}</div>
                                        )}
                                    </div>
                                )}
                                
                                {hasChanges && (
                                    <Alert variant="warning" className="mb-3">
                                        <small>You have unsaved changes</small>
                                    </Alert>
                                )}

                                <Form.Group>
                                    <Form.Label>Prompt Content</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={20}
                                        style={{ 
                                            minHeight: "500px",
                                            fontFamily: "monospace",
                                            fontSize: "14px",
                                            lineHeight: "1.4"
                                        }}
                                        value={promptText}
                                        onChange={this.handlePromptChange}
                                        disabled={!isEditing}
                                        placeholder="Prompt content will appear here..."
                                    />
                                    <Form.Text className="text-muted">
                                        Character count: {promptText.length}
                                    </Form.Text>
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    )}

                    {!selectedKey && (
                        <Card className="mt-4">
                            <Card.Body className="text-center text-muted">
                                <h5>No prompt selected</h5>
                                <p>Please select a prompt key from the dropdown above to view and edit its content.</p>
                            </Card.Body>
                        </Card>
                    )}
                </div>
            </div>
        );
    }
}

PromptsList.propTypes = {
    fetchPromptKeys: PropTypes.func.isRequired,
    fetchPromptByKey: PropTypes.func.isRequired,
    updatePromptByKey: PropTypes.func.isRequired,
    clearPrompts: PropTypes.func.isRequired,
    clearSinglePrompt: PropTypes.func.isRequired,
    promptKeys: PropTypes.array.isRequired,
    selectedPrompt: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
    promptKeys: state.prompts.promptKeys,
    selectedPrompt: state.prompts.selectedPrompt,
    loading: state.prompts.loading
});

const mapDispatchToProps = dispatch => ({
    fetchPromptKeys: bindActionCreators(fetchPromptKeys, dispatch),
    fetchPromptByKey: bindActionCreators(fetchPromptByKey, dispatch),
    updatePromptByKey: bindActionCreators(updatePromptByKey, dispatch),
    clearPrompts: bindActionCreators(clearPrompts, dispatch),
    clearSinglePrompt: bindActionCreators(clearSinglePrompt, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(PromptsList);
